/** Options for the /start-project enquiry wizard (see images/journey wireframes). */

export type WizardServiceId =
  | "website"
  | "eshop"
  | "portfolio"
  | "web-app"
  | "android-app"
  | "ios-app"
  | "custom-software"
  | "games-interactive"
  | "branding-identity"
  | "full-product";

export const WIZARD_SERVICES: {
  id: WizardServiceId;
  title: string;
  description: string;
}[] = [
  {
    id: "website",
    title: "Website",
    description: "Marketing sites and landing pages that convert and last.",
  },
  {
    id: "eshop",
    title: "E-shop",
    description: "Online stores with clear checkout and mobile shopping.",
  },
  {
    id: "portfolio",
    title: "Portfolio",
    description: "A personal site that does your work justice.",
  },
  {
    id: "web-app",
    title: "Web App",
    description: "Production-ready SaaS, dashboards and internal tools.",
  },
  {
    id: "android-app",
    title: "Android App",
    description: "Native-feeling Android apps, designed and shipped.",
  },
  {
    id: "ios-app",
    title: "iPhone / iOS App",
    description: "iOS apps that feel like they belong on the platform.",
  },
  {
    id: "custom-software",
    title: "Custom Software",
    description: "Tools tailored to a workflow, not a market.",
  },
  {
    id: "games-interactive",
    title: "Games & Interactive",
    description: "UI/UX, branding and front-end for games and interactive experiences.",
  },
  {
    id: "branding-identity",
    title: "Branding & Identity",
    description: "Logos, systems, voice — the whole feel.",
  },
  {
    id: "full-product",
    title: "Full Product Delivery",
    description: "From idea to shipped, end-to-end.",
  },
];

export const WIZARD_TIMELINES: { id: string; title: string; hint: string }[] = [
  { id: "asap", title: "As soon as possible", hint: "Within 4 weeks" },
  { id: "1-3-months", title: "In 1–3 months", hint: "Most common" },
  { id: "3-6-months", title: "In 3–6 months", hint: "Planning ahead" },
  { id: "exploring", title: "Just exploring", hint: "No fixed date" },
];

export const WIZARD_BUDGETS: { id: string; title: string; hint: string }[] = [
  { id: "under-5k", title: "Under €5k", hint: "Small focused engagement" },
  { id: "5k-15k", title: "€5k – €15k", hint: "Defined scope" },
  { id: "15k-40k", title: "€15k – €40k", hint: "Multi-month project" },
  { id: "40k-plus", title: "€40k+", hint: "Full product engagement" },
  { id: "unsure", title: "Not sure yet", hint: "Happy to scope together" },
];

export const WIZARD_STEPS = [
  { id: 1, label: "Services" },
  { id: 2, label: "Timeline" },
  { id: 3, label: "Budget" },
  { id: 4, label: "Goals" },
  { id: 5, label: "Project" },
  { id: 6, label: "Details" },
] as const;

/** Map product ids from /services/products to wizard service ids. */
export const SERVICE_PRODUCT_TO_WIZARD: Record<string, WizardServiceId> = {
  "micro-page": "website",
  website: "website",
  eshop: "eshop",
  portfolio: "portfolio",
  "web-app": "web-app",
  "mobile-app": "ios-app",
  "brand-identity": "branding-identity",
  "games-interactive": "games-interactive",
};

/** Map capability ids from /services/capabilities to wizard selections. */
export const SERVICE_CAPABILITY_TO_WIZARD: Record<string, WizardServiceId[]> = {
  "ux-strategy-and-design": ["website", "web-app"],
  "product-development": ["full-product", "web-app"],
  "design-only": ["website", "web-app"],
  "front-end-development": ["web-app", "custom-software"],
  "design-sprint": ["website"],
  "maintenance-support": ["website"],
};
